import classnames from 'classnames';

import { PriceDeprecated } from '@/core/tamagoshi/deprecated/PriceDeprecated/PriceDeprecated';
import { Text } from '@/core/tamagoshi/Text/Text';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { isB2B, isUkPlatform } from '@/domains/core/settings/utils';
import { Button } from '@/domains/core/tamagoshiv2/components/Button/Button';
import type { TaxKey } from '@/domains/productDiscovery/algolia/interfaces/product';
import { addToCart } from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/translations';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import { useIsPurchaseDisabledDueToCrossPlatform } from '@/domains/productDiscovery/ProductPage/hooks/useIsPurchaseDisabledDueToCrossPlatform/useIsPurchaseDisabledDueToCrossPlatform.default';
import { useProductBundle } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/context/ProductBundle/ProductBundle.context';
import { useWatchAddToCartRecommendations } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/hooks/useWatchAddToCartRecommendations/useWatchAddToCartRecommendations';
import { ECardCurrency } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/ProductBundle.interfaces';
import { getPrice } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/utils/formatters';
import {
  allTaxExcludedShort,
  allTaxIncludedShort,
} from '@/shoppingCart/CartPage/components/ProductCard/ProductPrices/translations';

import OutlineBundleA2CSkel from './OutlineBundleA2C.skel';
import {
  outlineBundleTranslationButton,
  outlineBundleTranslationTermsLabel,
  outlineBundleTranslationTotalPriceLabel,
} from './outlineBundleA2C.translations';

import styles from './outlineBundleA2C.module.scss';

const OutlineBundleA2C = () => {
  const {
    state: { isFetching: isLoading },
    productList,
    handleAddToCart,
  } = useProductBundle();

  const isDisabled = useIsPurchaseDisabledDueToCrossPlatform();

  useWatchAddToCartRecommendations(
    productList.map((product) => product.productId),
  );

  const totalPrice = getPrice(productList);
  const productsInTheBundle = productList.filter(
    (product) => product.isInBundle,
  ).length;

  const withVat: TaxKey =
    isUkPlatform() ||
    productList.some((product) => product.bundlePrices.taxKey === 'with_vat')
      ? 'with_vat'
      : 'without_vat';

  const buttonLabel = outlineBundleTranslationButton.format({
    quantity: productsInTheBundle,
  });
  const [isProductPageRevampEnabled] = useBooleanFlags([
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);

  return (
    <div
      className={
        isProductPageRevampEnabled ? styles.revampedWrapper : styles.wrapper
      }
    >
      <div
        className={classnames(styles.priceWrapper, {
          [styles.priceWrapperB2B]: isB2B(),
        })}
      >
        <Text tag="p" className={styles.totalLabel}>
          {outlineBundleTranslationTotalPriceLabel}
        </Text>
        {isLoading ? (
          <OutlineBundleA2CSkel />
        ) : (
          <>
            {isB2B() && (
              <Text tag="p" className={styles.vatLabel}>
                {withVat === 'with_vat'
                  ? allTaxIncludedShort
                  : allTaxExcludedShort}
              </Text>
            )}

            <PriceDeprecated
              className={styles.priceLabel}
              value={totalPrice}
              currency={isUkPlatform() ? ECardCurrency.GB : ECardCurrency.EU}
              data-testid="outline-bundle-a2c-total-price"
              supDecimal
            />
          </>
        )}
      </div>

      <div
        className={classnames(styles.buttonWrapper, {
          [styles.showButtonDesktop]: false,
          [styles.hideButtonDesktop]: true,
          [styles.showButtonNotDesktop]: true,
          [styles.hideButtonNotDesktop]: false,
        })}
      >
        <Button
          content={`${buttonLabel}`}
          data-testid="outline-bundle-a2c-button"
          className={styles.button}
          onClick={handleAddToCart}
          disabled={isLoading || isDisabled}
          size="L"
          width="fillContainer"
        />
        <p className={styles.termsLabel}>
          {outlineBundleTranslationTermsLabel}
        </p>
      </div>

      <div
        className={classnames(styles.buttonWrapper, {
          [styles.showButtonDesktop]: true,
          [styles.hideButtonDesktop]: false,
          [styles.showButtonNotDesktop]: false,
          [styles.hideButtonNotDesktop]: true,
        })}
      >
        <Button
          content={addToCart}
          className={styles.button}
          data-testid="outline-bundle-a2c-button"
          onClick={handleAddToCart}
          disabled={isLoading || isDisabled}
          width="fillContainer"
          size="L"
        />
        <p className={styles.termsLabel}>
          {outlineBundleTranslationTermsLabel}
        </p>
      </div>
    </div>
  );
};

OutlineBundleA2C.displayName = 'OutlineBundleA2C';

export default OutlineBundleA2C;
